import moment from 'moment'
import { reactive, toRefs, computed } from 'vue'
import { Message } from 'view-design'
import { mul } from '@/common/js/api'
import { queryLabelAnalysisDataByPage, exportLabelAnalysisTableData } from '@/api/report.model.js'
import { toThousands, toPercentFixed } from '@/common/js/filters'
import columns from '../../data/material_label_life_column.js'
import getChartOpts from '../data/label-analysis-chart-opts.js'
import { analysisDimensions } from '../data/analysis-dimensions.js'
import { clone } from '@/common/js/just.js'

export const DATE_TYPE = {
  HOUR: 0,
  DAY: 1,
  WEEK: 2,
  MONTH: 3,
  TOTAL: 4
}

const tableNumberFields = [
  'pv',
  'clickNum',
  'paperUseAmount',
  'amount',
  'newRoleMoney',
  'sumRecharge',
  'sumMoney',
  'downNum',
  'newUserNum',
  'createRoleNum',
  'newRoleNum',
  'payRoleUserNum',
  'timeDiff'
]
const tableRateFields = [
  'createRoleRate',
  'payRoleUserRate',
  'newRoleRate1d',
  'newRoleRate',
  'sumRebateRate',

  'createRoleTwoDayRetainRate',
  'createRoleThreeDayRetainRate',
  'createRoleFourDayRetainRate',
  'createRoleFiveDayRetainRate',
  'createRoleSixDayRetainRate',
  'createRoleSevenDayRetainRate',
  'createRoleTenDayRetainRate',
  'createRoleFifteenDayRetainRate',
  'createRoleThirtyDayRetainRate',

  'createRoleRoi1d',
  'createRoleRoi3d',
  'createRoleRoi7d',
  'createRoleRoi15d',
  'createRoleRoi30d',

  'twoDayNewPayRoleRetain',
  'threeDayNewPayRoleRetain',
  'fourDayNewPayRoleRetain',
  'fiveDayNewPayRoleRetain',
  'sixDayNewPayRoleRetain',
  'sevenDayNewPayRoleRetain',
  'tenDayNewPayRoleRetain',
  'fifteenDayNewPayRoleRetain',
  'thirtyDayNewPayRoleRetain'
]

const isValidAmount = (value) => value === undefined || value === null || value === '' || (Number.isInteger(value) && value >= 0)
const isValidNewRoleRate = (value) =>
  value === undefined || value === null || value === '' || (typeof value === 'number' && value >= 0 && value <= 100)
const isEmptyCompareValue = (value) => value === undefined || value === null || value === ''

export const useTable = (queryFormRef, outsideQueryForm, dimensionList) => {
  const tableModel = reactive({
    tableData: [],
    tableSumData: [],
    // 保存本次查询的的维度
    currQueryDimList: [],
    // dateType: DATE_TYPE.DAY,
    total: 0,
    sortParams: {
      sort: '',
      order: ''
    },
    page: {
      pageId: 1,
      pageSize: 50
    },
    tableLoading: false,
    exportLoading: false,
    chartDimension: 'amount', // 图表维度：默认实际消耗
    echartsOptions: null,
    showCustomTableColumnModal: false,
    tableColumns: [],
    originQueryForm: {}
  })

  const tableCols = computed(() => {
    // 维度列追加到所有列的最前面
    const dimCols = analysisDimensions.filter(d => tableModel.currQueryDimList.includes(d.key)).map(dim => {
      return {
        title: dim.label,
        key: dim.tableField,
        width: 140,
        fixed: true,
        checked: true,
        sortable: 'custom'
      }
    })

    const checkedCols = tableModel.tableColumns.filter(col => col.checked)

    const cols = [...dimCols, ...checkedCols]
    return cols
  })

  /**
   * @description: 获取表格数据
   * @return {*}
   */
  const getPageData = () => {
    const params = buildParams()

    if (tableModel.tableLoading) return
    tableModel.tableLoading = true
    tableModel.currQueryDimList = dimensionList.value

    queryLabelAnalysisDataByPage({ ...tableModel.page, ...params, ...tableModel.sortParams }).then((data) => {
      const { list = [], page_info = {}, sum_data = {} } = data || {}

      tableModel.tableData = list
      tableModel.tableSumData = sum_data
      tableModel.total = page_info.total_number || 0

      initialChart()
    }).finally(() => {
      tableModel.tableLoading = false
    })
  }

  const tableSummaryMethod = ({ columns }) => {
    const data = tableModel.tableSumData
    const sums = []
    columns.forEach((column, index) => {
      const prop = column.property
      if (index === 0) {
        sums[index] = '合计'
        return
      }

      // 格式化数据格式
      if (tableNumberFields.includes(prop)) sums[index] = toThousands(data[prop])
      else if (tableRateFields.includes(prop)) sums[index] = toPercentFixed(data[prop])
      else sums[index] = data[prop]
    })
    return sums
  }

  const onTableSortAction = (params) => {
    const { prop, order } = params
    let orderText = ''
    if (order === 'ascending') orderText = 'asc'
    if (order === 'descending') orderText = 'desc'

    tableModel.sortParams.sort = prop
    tableModel.sortParams.order = orderText
    tableModel.page.pageId = 1
    getPageData()
  }

  const onTableExportAction = () => {
    const exportColumnOrderMap = {}
    tableCols.value.forEach((col) => {
      if (col.checked && !col.exclude_export) exportColumnOrderMap[col.key] = col.title
    })
    const params = {
      ...buildParams(),
      ...tableModel.sortParams,
      exportColumnOrderMap
    }

    if (tableModel.exportLoading) return
    tableModel.exportLoading = true
    exportLabelAnalysisTableData(params).then(data => {
      if (data.msg) {
        Message.info(data.msg)
      }
    }).finally(() => {
      tableModel.exportLoading = false
    })
  }

  function buildParams() {
    const formData = queryFormRef.value.getForm()
    const { dateRange, weekRange, monthRange, dateType } = outsideQueryForm.value
    const {
      platform,
      threeDLabelIds,
      contentLabelIds,
      imageStyles,
      mediaId,
      gameGroups,
      dataGroupIds,
      imgTypes,
      amountOperatorType,
      amountValue,
      newRoleRateOperatorType,
      newRoleRateValue
    } = formData
    const imageStyleIds = imageStyles.length ? imageStyles.map((i) => i.slice(-1)).flat() : []
    const [beginTime, endTime] = dateRange || []
    let dates = { beginTime, endTime }

    if (dateType === DATE_TYPE.WEEK) {
      const [b, e] = weekRange.map((d) => moment(d).format('YYYY-MM-DD'))
      dates = { beginTime: b, endTime: e }
    }

    if (dateType === DATE_TYPE.MONTH) {
      const [b, e] = monthRange.map((d) => moment(d).format('YYYY-MM-DD'))
      dates = { beginTime: b, endTime: e }
    }

    const labelIdsStr = [contentLabelIds, threeDLabelIds, imageStyleIds]
      .filter((i) => Array.isArray(i))
      .flat()
      .join(',')

    tableModel.originQueryForm = { ...formData }

    const params = {
      ...dates,
      dateType,
      labelIdsStr,
      imgTypes,
      dataGroupIds,
      mediaId,
      gameGroupIdStr: gameGroups.join(','),
      platform
    }

    if (!isEmptyCompareValue(amountValue) && isValidAmount(amountValue)) {
      params.amountCompare = { operatorType: amountOperatorType, value1: amountValue }
    }
    if (!isEmptyCompareValue(newRoleRateValue) && isValidNewRoleRate(newRoleRateValue)) {
      params.newRoleRateCompare = { operatorType: newRoleRateOperatorType, value1: newRoleRateValue }
    }

    const dimList = analysisDimensions.map(d => d.key)
    for (const i in dimensionList.value) {
      const dim = dimensionList.value[i]
      if (dimList.includes(dim)) {
        params[dim] = 1
      }
    }
    return params
  }

  function initialChart() {
    const { chartDimension, currQueryDimList } = tableModel
    const chartDim = [chartDimension]

    // 找到最后一个选中的维度项
    const lastSeletedDim = currQueryDimList.slice(-1)[0]
    const { tableField: needle } = analysisDimensions?.find(dim => dim.key === lastSeletedDim) || {}
    // 复制一个副本，防止sort时改变元数据
    const tableData = clone(tableModel.tableData)

    const legends = Array.from(new Set(tableData.map((row) => row[needle])))
    const { dateType } = outsideQueryForm.value
    const xAxisData =
      dateType === DATE_TYPE.TOTAL
        // 如果dateType = 汇总统计，x轴只取一个点，就是统计的时间段，tableList[0].tdateDesc
        ? [tableData[0].tdateDesc]
        : Array.from(new Set(tableData.sort((a, b) => a.tdate - b.tdate).map((row) => row.tdateDesc)))
    const yAxis = chartDim.map((dim) => {
      const column = columns.filter((t) => t.key === dim).pop()
      let unit = ''
      if (dim.includes('Rate')) unit = '%'
      else if (['paperUseAmount', 'amount', 'activeCost', 'createRoleCost', 'sumRecharge', 'sumMoney'].includes(dim)) unit = '元'
      return {
        name: column.title,
        nameGap: 5,
        type: 'value',
        axisLabel: {
          formatter: `{value} ${unit}`
        }
      }
    })

    const series = legends.map((legend) => {
      return {
        name: legend,
        data: tableData
          .filter((row) => row[needle] === legend)
          .sort((a, b) => a.tdate - b.tdate)
          .map((row) => (chartDimension.includes('Rate') ? mul(100, row[chartDimension]) : row[chartDimension])),
        type: 'line'
      }
    })

    const echartsOptions = getChartOpts(chartDimension, legends, xAxisData, yAxis, series)
    tableModel.echartsOptions = echartsOptions
  }

  function onTableColsChange(cols) {
    tableModel.showCustomTableColumnModal = false
    tableModel.tableColumns = cols
  }

  return {
    ...toRefs(tableModel),
    tableCols,
    tableNumberFields,
    tableRateFields,
    getPageData,
    initialChart,
    buildParams,
    tableSummaryMethod,
    onTableSortAction,
    onTableExportAction,
    onTableColsChange
  }
}

export const useDateRange = () => {
  const monthDefaultValue = moment().subtract(1, 'years').toDate()
  const weekDefaultValue = moment().subtract(1, 'month').toDate()

  const weekOptions = {
    firstDayOfWeek: 1,
    cellClassName: (date) => {
      if (date && date.valueOf() > Date.now()) return 'empty-date'
      return ''
    },
    disabledDate: (date) => {
      const isMonday = new Date().getDay() === 1
      if (isMonday) {
        return date && date.valueOf() > Date.now()
      }
      return date && date.valueOf() > Date.now() + (7 - new Date().getDay()) * 1000 * 60 * 60 * 24
    }
  }
  const monthOptions = { disabledDate }

  function disabledDate(date) {
    return date && date.valueOf() > Date.now() - 1000 * 60 * 60 * 24
  }

  return {
    weekDefaultValue,
    monthDefaultValue,
    weekOptions,
    monthOptions,
    disabledDate
  }
}
